import React from "react";

// Severity token maps ----------------------------------------------------
export const SEV = {
  crit: { color: "var(--sv-crit)", bg: "var(--sv-crit-bg)", label: "Critical" },
  risk: { color: "var(--sv-risk)", bg: "var(--sv-risk-bg)", label: "At risk" },
  watch: { color: "var(--sv-watch)", bg: "var(--sv-watch-bg)", label: "Watch" },
  good: { color: "var(--sv-good)", bg: "var(--sv-good-bg)", label: "Healthy" },
};

export const dotColor = (k) =>
  ({ crit: "var(--sv-crit)", risk: "var(--sv-risk)", watch: "var(--sv-watch)",
     good: "var(--sv-good)", indigo: "var(--ac-indigo)", lo: "var(--ui-line)" }[k] ||
    "var(--ui-line)");

export const fmt$ = (n) =>
  "$" + Math.round(n || 0).toLocaleString("en-AU");
export const fmtN = (n) => Math.round(n || 0).toLocaleString("en-AU");

// Datetime — always rendered in Australia/Brisbane, e.g. "25 Aug 2026, 3:04 AM"
const BNE = "Australia/Brisbane";
export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const d = new Date(s.includes("T") ? s : `${s}T00:00:00+10:00`);
  if (isNaN(d.getTime())) return s;
  const date = new Intl.DateTimeFormat("en-AU", { timeZone: BNE, day: "numeric", month: "short", year: "numeric" }).format(d);
  const time = new Intl.DateTimeFormat("en-AU", { timeZone: BNE, hour: "numeric", minute: "2-digit", hour12: true }).format(d);
  return `${date}, ${time.replace(/\s+/g, " ").toUpperCase()}`;
};
export const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const d = new Date(s.includes("T") ? s : `${s}T00:00:00+10:00`);
  if (isNaN(d.getTime())) return s;
  return new Intl.DateTimeFormat("en-AU", { timeZone: BNE, day: "numeric", month: "short", year: "numeric" }).format(d);
};

// Card -------------------------------------------------------------------
export function Card({ children, className = "", style = {}, ...rest }) {
  return (
    <div
      className={`rounded-xl border ${className}`}
      style={{
        background: "var(--ui-card)",
        borderColor: "var(--ui-line-soft)",
        boxShadow: "var(--shadow-card)",
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

// Buttons ----------------------------------------------------------------
export function Button({ children, variant = "primary", className = "", ...rest }) {
  const base = "prose-i inline-flex items-center justify-center font-medium transition-colors";
  const styles =
    variant === "primary"
      ? { background: "var(--ac-indigo)", color: "var(--ui-on-accent)",
          boxShadow: "var(--glow-indigo)", borderRadius: "10px",
          padding: "10px 18px", fontSize: 14 }
      : { background: "var(--ui-card-2)", color: "var(--ui-text-mid)",
          border: "1px solid var(--ui-line)", borderRadius: "10px",
          padding: "9px 16px", fontSize: 14 };
  return (
    <button className={`${base} ${className}`} style={styles} {...rest}>
      {children}
    </button>
  );
}

// Severity pill ----------------------------------------------------------
export function SeverityPill({ severity = "good", label }) {
  const s = SEV[severity] || SEV.good;
  return (
    <span
      className="inline-flex items-center gap-2"
      style={{
        background: s.bg, border: `1px solid ${s.color}`, color: s.color,
        borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 500,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: 9999,
        background: s.color, display: "inline-block" }} />
      {label || s.label}
    </span>
  );
}

// Toggle 40x22 -----------------------------------------------------------
export function Toggle({ on, onChange, severity, disabled, ...rest }) {
  const onColor = severity ? SEV[severity]?.color : "var(--ac-indigo)";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange && onChange(!on)}
      {...rest}
      style={{
        width: 40, height: 22, borderRadius: 12, position: "relative",
        background: on ? (onColor || "var(--ac-indigo)") : "var(--ui-track)",
        border: on ? "none" : "1px solid var(--ui-line)",
        opacity: disabled ? 0.4 : 1, cursor: disabled ? "not-allowed" : "pointer",
        transition: "background .15s",
      }}
      aria-pressed={on}
    >
      <span
        style={{
          position: "absolute", top: 3, left: on ? 21 : 3,
          width: 16, height: 16, borderRadius: 9999,
          background: on ? "var(--ui-on-accent)" : "var(--ui-text-lo)",
          transition: "left .15s",
        }}
      />
    </button>
  );
}

// Progress bar 6px -------------------------------------------------------
export function ProgressBar({ pct, severity = "good" }) {
  const color = SEV[severity]?.color || "var(--sv-good)";
  return (
    <div style={{ height: 6, borderRadius: 4, background: "var(--ui-track)", overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${Math.max(2, Math.min(100, pct))}%`,
        background: color, borderRadius: 4 }} />
    </div>
  );
}
